import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { ProductDraft } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
  transformSearch,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminProductsDraftsGet>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<ProductDraft>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminProductsDraftsGet {
  limit: number;
  offset: number;
  sort: string;
  manufacturerId: number;
  typeId: number;
  name: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  manufacturerId: 'number',
  typeId: 'number',
  name: 'string',
}

export const useProductDraftListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    transformSearch('name'),
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminProductsDraftsGet>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<ProductDraft>>(
    [Entities.ProductsDrafts, JSON.stringify(transformedParams)],
    async () => {
      const {
        limit,
        offset,
        sort,
        manufacturerId,
        typeId,
        name,
      } = queryParams
      const { data } = await api.v1AdminProductsDraftsGet(
        limit,
        offset,
        sort,
        manufacturerId,
        typeId,
        name,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
